///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type Database } from "@/earthbrowser/database/Database"
import { StormCloudsTable, type StormCloudProperties } from "@/earthbrowser/dataset-distribution/clouds/StormCloudsTable"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineActions, engineBridge, isPaidEntitlement } from "@/earthbrowser/engineBridge"
import { isVideoRenderMode } from "@/earthbrowser/utils/renderMode"
import { engineNotifier, type StormAdvisoryNotification } from "@/earthbrowser/engineNotifications"
import { BuoyObservationsTable } from "@/earthbrowser/dataset-distribution/buoys/BuoyObservationsTable"
import { BuoyStationsTable } from "@/earthbrowser/dataset-distribution/buoys/BuoyStationsTable"
import { SstTable } from "@/earthbrowser/dataset-distribution/sst/SstTable"
import { type StormCutoutBestTrack, type StormCutoutBuoy, type StormCutoutEnvironment, type StormCutoutForecast, type StormCutoutShips, type StormCutoutSummary, type StormCutoutUpdate } from "@/redux/dialogSlice"
import { glmCovers, lightningSummary, StormLightningSource, type StormColumnFlash } from "./column/stormColumnLightning"
import { logger } from "@/utils/LogDispatcher"
import { trig } from "@stovokor/math/math"
import { DatasetLayer, type ArrowRow, type DatasetDelta, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../DatasetLayer"
import { StormCloudField } from "./column/StormCloudField"
import { StormColumnNode } from "./column/StormColumnNode"
import { environmentFacts } from "./column/stormColumnEnvironment"
import { blockToLatLon, buildStormColumnLayout, insideColumn, latLonToBlock, localOffsetKm, type FrameBox, type StormColumnLayout } from "./column/stormColumnLayout"
import { StormSstField, type LatLonBox } from "./column/StormSstField"
import { ensembleIntensitySpread, fitWindModel, integratedKineticEnergyTJ, radius34Summary, windChange24h } from "./column/stormColumnStats"
import { HurricanesTable, parseBestTrack, parseEnsembleTracks, parseShips, parseTrack, parseWindProbability, parseWindRadii, type BestTrack, type ShipsDiagnostics, type StormProperties, type StormTrackPoint } from "./HurricanesTable"
import { type WindProbabilityThreshold } from "./windProbability"
import { StormFeature } from "./StormFeature"
import { findLandfall } from "./stormTrack"
import { geographic } from "@/earthbrowser/utils/geographic"

// Storm-centered IR frames for the column lid: every frame over the most
// recent stretch, then hourly further back, within the tier's history
// window (CloudsDatasetLayer keeps the same 2 h / 48 h depth)
const cColumnDenseHours = 2
const cColumnSparseMinutes = 60
const cColumnFrameConcurrency = 3
/** Buoy pins drawn inside the block (nearest first) */
const cColumnMaxBuoys = 12
/** Half-width of the storm-centered IR frame box the clouds pipeline cuts (storm-clouds.ts BOX_HALF_DEG) */
const cStormFrameHalfDeg = 8

function columnHistoryHours(): number {
	return isPaidEntitlement() ? 48 : 3
}

/** Lat/lon box enclosing the rotated footprint, padded 5% */
function columnLatLonRegion(layout: StormColumnLayout): LatLonBox {
	const region = { minLon: Infinity, minLat: Infinity, maxLon: -Infinity, maxLat: -Infinity }
	for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
		const ll = blockToLatLon(layout, sx * layout.lengthKm / 2 * 1.05, sz * layout.widthKm / 2 * 1.05)
		region.minLon = Math.min(region.minLon, ll.lon)
		region.maxLon = Math.max(region.maxLon, ll.lon)
		region.minLat = Math.min(region.minLat, ll.lat)
		region.maxLat = Math.max(region.maxLat, ll.lat)
	}
	return region
}

/** Shape of a hurricanes Arrow row (only fields we read) */
type StormRow = {
	id: string
	name: string
	basin: string
	storm_num: number
	classification: string
	advisory_num: string | null
	advisory_date: string
	lat: number
	lon: number
	max_wind_kt: number | null | undefined
	gust_kt: number | null | undefined
	mslp_mb: number | null | undefined
	category: number | null
	movement_dir: number | null | undefined
	movement_speed_kt: number | null | undefined
	observed_track: string | null
	forecast_track: string | null
	cone: string | null
	watches_warnings: string | null
	wind_radii: string | null
	headline: string | null
	ensemble_tracks?: string | null
	ships?: string | null
	best_track?: string | null
	wind_probability?: string | null
	bbox_min_lon: number
	bbox_min_lat: number
	bbox_max_lon: number
	bbox_max_lat: number
	update_key: string
	ingested_at: string
}

export class HurricanesDatasetLayer extends DatasetLayer<StormProperties> {
	private unsubscribeRedux?: () => void

	/** Open storm column (at most one) and the storm it belongs to */
	private column?: StormColumnNode
	private columnStormId?: string
	private columnFields: StormCloudField[] = []
	private columnSst?: StormSstField
	private columnEnvironment?: StormCutoutEnvironment
	private columnBuoys: StormCutoutBuoy[] = []
	/** GLM flashes for the column's storm (oldest first) and whether the shard existed */
	private columnFlashes?: { flashes: StormColumnFlash[]; observed: StormTrackPoint[] }
	/** Bumped on every open/close so stale async steps bail out */
	private columnToken = 0
	private windProbabilityKt: WindProbabilityThreshold = 34

	/** Live instance for the dev debug hooks */
	public static live?: HurricanesDatasetLayer

	constructor(context: EarthBrowserContext) {
		super(context, "hurricanes")
		HurricanesDatasetLayer.live = this
		this.subscribeToVisibility()
	}

	private subscribeToVisibility(): void {
		this.visible = engineBridge.getState()?.earthbrowser.visibleDatasets.includes("hurricane") ?? false
		this.unsubscribeRedux = engineBridge.subscribe(() => {
			const state = engineBridge.getState()
			const isVisible = state?.earthbrowser.visibleDatasets.includes("hurricane") ?? false
			if (isVisible !== this.visible) {
				this.visible = isVisible
				if (!isVisible && this.columnStormId) {
					// Layer switched off under an open column: tear it down and
					// hand the camera back to Earth where it stands
					this.closeColumn(false)
					engineBridge.dispatch({ type: engineActions.setStormCutout, payload: null })
				}
			}

			// Wind probability threshold: every storm's swath and the open column follow it
			const kt = state?.dialog.stormWindProbabilityKt ?? 34
			if (kt !== this.windProbabilityKt) {
				this.windProbabilityKt = kt
				for (const object of this.sceneObjects.values()) {
					if (object instanceof StormFeature) object.setWindProbabilityThreshold(kt)
				}
				this.column?.setWindProbabilityThreshold(kt)
			}

			const wantId = state?.dialog.stormCutout?.stormId
			if (wantId !== this.columnStormId) {
				if (this.columnStormId) {
					this.closeColumn(wantId === undefined)
				}
				if (wantId) {
					void this.openColumn(wantId)
				}
			}
		})
	}

	override destroy(): void {
		this.unsubscribeRedux?.()
		this.closeColumn(false)
		if (HurricanesDatasetLayer.live === this) {
			HurricanesDatasetLayer.live = undefined
		}
		super.destroy()
	}

	// ==================== Storm column ====================

	/**
	 * Build the storm column for a storm. The block goes up synchronously
	 * from the advisory row alone (wind fit, forecast, tracks, probability
	 * contours, best-track rings) and the camera flies to it; nothing on
	 * that path touches the database, whose queue can be seconds deep
	 * behind a background ingest. The IR frames, SST, buoys and lightning
	 * then stream in, each landing its own HUD stage as it arrives
	 */
	private async openColumn(stormId: string): Promise<void> {
		const token = ++this.columnToken
		this.columnStormId = stormId

		const sceneObject = this.getSceneObject(stormId) ?? await this.loadSceneObjectById(stormId)
		if (token !== this.columnToken) return
		if (!(sceneObject instanceof StormFeature)) {
			this.updateColumnState(stormId, { status: "error" })
			return
		}
		const storm = sceneObject.feature
		const observed = parseTrack(storm.observed_track)
		const forecast = parseTrack(storm.forecast_track)
		const radii = parseWindRadii(storm.wind_radii)

		this.updateColumnState(stormId, {
			status: "loading",
			storm: {
				name: storm.name,
				classification: storm.classification,
				basin: storm.basin,
				category: storm.category,
				maxWindKt: storm.max_wind_kt,
				gustKt: storm.gust_kt,
				mslpMb: storm.mslp_mb,
				advisoryNum: storm.advisory_num,
				advisoryDate: storm.advisory_date,
				lat: storm.lat,
				lon: storm.lon,
				movementDir: storm.movement_dir,
				movementSpeedKt: storm.movement_speed_kt,
				change24hKt: windChange24h(observed)
			}
		})

		// The storm-cloud pipeline always cuts its IR frames 8° either side
		// of the storm, so the footprint can be sized to that box without
		// waiting for the frame rows
		const frameBox: FrameBox = {
			minLon: storm.lon - cStormFrameHalfDeg, maxLon: storm.lon + cStormFrameHalfDeg,
			minLat: storm.lat - cStormFrameHalfDeg, maxLat: storm.lat + cStormFrameHalfDeg
		}
		const layout = buildStormColumnLayout(storm, radii, observed, frameBox)
		// The best track's RMW (NHC basins) pins the eyewall; otherwise climatology
		const bestTrack = parseBestTrack(storm.best_track)
		const newestFix = bestTrack?.fixes[bestTrack.fixes.length - 1]
		const windModel = fitWindModel(storm.max_wind_kt, storm.lat, radii, newestFix?.rmwKm)

		const node = new StormColumnNode(this.context, storm, layout, windModel, observed, forecast)
		node.onDismissed = () => this.handleColumnDismissed(node)
		if (newestFix?.rociKm) node.setOuterClosedIsobar(newestFix.rociKm)
		const windProbability = parseWindProbability(storm.wind_probability)
		if (windProbability) node.setWindProbability(windProbability, this.windProbabilityKt)
		node.onFrameChanged = () => {
			if (this.column === node) this.updateColumnState(stormId, { summary: this.summarize(node) })
		}
		this.column = node
		this.addNode(node)
		const ships = shipsSummary(parseShips(storm.ships))
		if (ships?.shearKt !== null && ships?.shearKt !== undefined && ships.shearDirDeg !== null) {
			node.setShear(ships.shearKt, ships.shearDirDeg)
		}
		sceneObject.setColumnOpen(true)
		// The video renderer scripts the camera itself and the block is a
		// scene prop there: no focus flight (whose loss would also dismiss
		// the block), the Earth keeps the camera
		if (!isVideoRenderMode()) {
			this.context.focusManager.requestFocus(node)
		}
		this.updateColumnState(stormId, {
			status: "ready",
			stages: { wind: true, forecast: true },
			summary: this.summarize(node, this.forecastSummary(storm, observed, forecast))
		})

		// Everything that needs the database or the network streams in
		// alongside each other; each lands its own stage
		void this.loadColumnFrames(node, token, stormId)
		void this.loadColumnSst(node, token, stormId, observed, forecast)
		void this.loadColumnBuoys(node, token, stormId)
		void this.loadColumnLightning(node, token, stormId, observed)
	}

	/**
	 * IR frames for the lid: every frame over the recent stretch, hourly
	 * beyond, decoded newest first so the lid rises as soon as one lands
	 */
	private async loadColumnFrames(node: StormColumnNode, token: number, stormId: string): Promise<void> {
		let frameRows: StormCloudProperties[] = []
		try {
			const cutoff = new Date(Date.now() - columnHistoryHours() * 3600_000).toISOString()
			frameRows = await StormCloudsTable.getForStorm(stormId, cutoff)
		} catch (error) {
			logger.warn("[HurricanesDatasetLayer] storm column frame query failed", error)
		}
		if (token !== this.columnToken || this.column !== node) return
		if (frameRows.length === 0) {
			this.updateColumnState(stormId, { stages: { frames: true }, summary: this.summarize(node) })
			return
		}

		const selected = selectColumnFrames(frameRows)
		let index = 0
		const worker = async (): Promise<void> => {
			while (index < selected.length) {
				const row = selected[index++]
				const field = await StormCloudField.load(row)
				if (token !== this.columnToken || this.column !== node) {
					field?.destroy()
					return
				}
				if (!field) continue
				this.columnFields.push(field)
				node.setFields(this.columnFields)
				this.updateColumnState(stormId, { stages: { frames: true }, summary: this.summarize(node) })
			}
		}
		await Promise.all(Array.from({ length: cColumnFrameConcurrency }, () => worker()))
		if (token === this.columnToken && this.column === node) {
			this.updateColumnState(stormId, { stages: { frames: true }, summary: this.summarize(node) })
		}
	}

	/**
	 * Newest CoralTemp frame cut to the footprint (plus one about a day
	 * older for the cold wake): slab faces, floor tint and the Environment
	 * facts. Quietly skipped when the SST dataset hasn't synced
	 */
	private async loadColumnSst(node: StormColumnNode, token: number, stormId: string, observed: StormTrackPoint[], forecast: StormTrackPoint[]): Promise<void> {
		try {
			const records = await SstTable.getRecent(6)
			if (token !== this.columnToken || this.column !== node) return
			if (records.length === 0) {
				this.updateColumnState(stormId, { stages: { sst: true } })
				return
			}
			const newest = records[0]
			const newestMs = Date.parse(newest.data_timestamp)
			const earlier = records.find(r => newestMs - Date.parse(r.data_timestamp) >= 20 * 3600_000)
			const region = columnLatLonRegion(node.layout)
			const [now, before] = await Promise.all([
				StormSstField.load(newest, region),
				earlier ? StormSstField.load(earlier, region) : Promise.resolve(undefined)
			])
			if (token !== this.columnToken || this.column !== node) {
				now?.destroy()
				before?.destroy()
				return
			}
			if (!now) {
				this.updateColumnState(stormId, { stages: { sst: true } })
				return
			}
			this.columnSst = now
			node.setSst(now)
			const facts = environmentFacts(now, before, { lat: node.storm.lat, lon: node.storm.lon }, observed, forecast)
			before?.destroy()
			this.columnEnvironment = {
				frameIso: now.timeIso,
				sstUnderCenterC: facts.sstUnderCenterC,
				warmWaterEndsT: facts.warmWaterEndsT,
				alreadyCool: facts.alreadyCool,
				coldWakeC: facts.coldWakeC,
				wakeSpanHours: earlier ? Math.round((newestMs - Date.parse(earlier.data_timestamp)) / 3.6e6) : undefined
			}
			this.updateColumnState(stormId, { stages: { sst: true }, summary: this.summarize(node) })
		} catch (error) {
			logger.warn("[HurricanesDatasetLayer] storm column SST failed", error)
			if (token === this.columnToken) this.updateColumnState(stormId, { stages: { sst: true } })
		}
	}

	/** Buoys inside the footprint with their latest observation, as pins and a HUD row */
	private async loadColumnBuoys(node: StormColumnNode, token: number, stormId: string): Promise<void> {
		try {
			const region = columnLatLonRegion(node.layout)
			const stations = await BuoyStationsTable.getInBBox(region.minLon, region.minLat, region.maxLon, region.maxLat, 60)
			if (token !== this.columnToken || this.column !== node) return
			const inside = stations.filter(station => {
				const xz = latLonToBlock(node.layout, station.lat, station.lon)
				return insideColumn(node.layout, xz.x, xz.z, -10)
			})
			// One query for every station's latest report. Stations without
			// one still get a pin (a DART buoy under a storm is worth knowing
			// about); the label says so
			const latest = await BuoyObservationsTable.getLatestForStations(inside.map(s => s.station_id))
			if (token !== this.columnToken || this.column !== node) return
			const buoys: StormCutoutBuoy[] = []
			for (const station of inside) {
				const obs = latest.get(station.station_id)
				const lat = obs?.lat ?? station.lat
				const lon = obs?.lon ?? station.lon
				const local = localOffsetKm(lat, lon, node.storm.lat, node.storm.lon)
				buoys.push({
					stationId: station.station_id,
					name: station.name,
					lat,
					lon,
					time: obs?.time ?? null,
					pressureHpa: obs?.pressure ?? null,
					windGustMps: obs?.wind_gust ?? null,
					windSpeedMps: obs?.wind_speed ?? null,
					waveHeightM: obs?.wave_height ?? null,
					waterTempC: obs?.water_temp ?? null,
					distanceKm: Math.round(Math.hypot(local.east, local.north))
				})
			}
			buoys.sort((a, b) => a.distanceKm - b.distanceKm)
			this.columnBuoys = buoys.slice(0, cColumnMaxBuoys)
			node.setBuoys(this.columnBuoys)
			this.updateColumnState(stormId, { stages: { buoys: true }, summary: this.summarize(node) })
		} catch (error) {
			logger.warn("[HurricanesDatasetLayer] storm column buoy query failed", error)
			if (token === this.columnToken) this.updateColumnState(stormId, { stages: { buoys: true } })
		}
	}

	/**
	 * GOES GLM flashes for the storm from the clouds dataset's lightning
	 * shard: sparks on the lid and the hourly inner-core / outer-band
	 * counts. Storms outside both GOES fields of view get an explicit
	 * "no coverage" rather than a silent zero
	 */
	private async loadColumnLightning(node: StormColumnNode, token: number, stormId: string, observed: StormTrackPoint[]): Promise<void> {
		try {
			const result = await StormLightningSource.flashesForStorm(stormId)
			if (token !== this.columnToken || this.column !== node) return
			// Only what the block contains: the shard covers the 16° storm box,
			// and distant ITCZ or land convection would swamp the counts
			const inBlock = result.flashes.filter(f => {
				const xz = latLonToBlock(node.layout, f.lat, f.lon)
				return insideColumn(node.layout, xz.x, xz.z)
			})
			this.columnFlashes = { flashes: inBlock, observed }
			if (inBlock.length > 0) node.setLightning(inBlock)
			this.updateColumnState(stormId, { stages: { lightning: true }, summary: this.summarize(node) })
		} catch (error) {
			logger.warn("[HurricanesDatasetLayer] storm column lightning failed", error)
			if (token === this.columnToken) this.updateColumnState(stormId, { stages: { lightning: true } })
		}
	}

	/**
	 * Remove the block. With refocusStorm the camera flies back down to the
	 * storm marker (the user toggled the column off); otherwise focus is
	 * released in place
	 */
	private closeColumn(refocusStorm: boolean): void {
		const node = this.column
		const stormId = this.columnStormId
		this.column = undefined
		this.columnStormId = undefined
		this.columnToken++
		this.releaseColumnData()
		if (stormId) {
			const feature = this.getSceneObject(stormId)
			if (feature instanceof StormFeature) feature.setColumnOpen(false)
		}
		if (!node) return

		node.onDismissed = undefined
		const focusManager = this.context.focusManager
		if (focusManager.getFocusedNode() === node) {
			const feature = refocusStorm && stormId ? this.getSceneObject(stormId) : undefined
			if (feature instanceof StormFeature) {
				feature.requestFocus()
			} else {
				focusManager.releaseFocus({ preserveCurrentState: true })
			}
		}
		this.removeNode(node)
		node.destroy()
	}

	/** Focus moved elsewhere on its own (user clicked away): drop the block quietly */
	private handleColumnDismissed(node: StormColumnNode): void {
		if (this.column !== node) return
		const stormId = this.columnStormId
		this.column = undefined
		this.columnStormId = undefined
		this.columnToken++
		this.releaseColumnData()
		if (stormId) {
			const feature = this.getSceneObject(stormId)
			if (feature instanceof StormFeature) feature.setColumnOpen(false)
		}
		this.removeNode(node)
		node.destroy()
		engineBridge.dispatch({ type: engineActions.setStormCutout, payload: null })
	}

	private releaseColumnData(): void {
		for (const field of this.columnFields) field.destroy()
		this.columnFields = []
		this.columnSst?.destroy()
		this.columnSst = undefined
		this.columnEnvironment = undefined
		this.columnBuoys = []
		this.columnFlashes = undefined
		StormLightningSource.release()
	}

	private updateColumnState(stormId: string, fields: Omit<StormCutoutUpdate, "stormId">): void {
		engineBridge.dispatch({ type: engineActions.updateStormCutout, payload: { stormId, ...fields } })
	}

	/** Intensity series and ensemble spread for the HUD chart */
	private forecastSummary(storm: StormProperties, observed: StormTrackPoint[], forecast: StormTrackPoint[]): StormCutoutForecast {
		const series = (points: StormTrackPoint[]): Array<[number, number]> =>
			points.filter(p => p.wind_kt !== null).map(p => [p.t, p.wind_kt!])
		const windPressure: Array<[number, number, number]> = observed
			.filter(p => p.wind_kt !== null && p.mslp_mb !== null && p.mslp_mb > 850)
			.map(p => [p.t, p.wind_kt!, p.mslp_mb!])
		if (storm.max_wind_kt !== null && storm.mslp_mb !== null) {
			windPressure.push([Date.parse(storm.advisory_date), storm.max_wind_kt, storm.mslp_mb])
		}
		const result: StormCutoutForecast = {
			observed: series(observed),
			forecast: series(forecast),
			windPressure
		}
		const ships = parseShips(storm.ships)
		if (ships) {
			const runMs = Date.parse(ships.run)
			const line: Array<[number, number]> = []
			ships.taus.forEach((tau, i) => {
				const kt = ships.vmaxKt[i]
				if (kt !== null && kt !== undefined && Number.isFinite(runMs)) line.push([runMs + tau * 3600_000, kt])
			})
			if (line.length >= 2) result.ships = line
		}
		let peak: { kt: number; t: number } | undefined
		for (const [t, kt] of result.forecast) {
			if (!peak || kt > peak.kt) peak = { kt, t }
		}
		result.peak = peak
		const ensemble = parseEnsembleTracks(storm.ensemble_tracks)
		if (ensemble) {
			const spread = ensembleIntensitySpread(ensemble)
			if (spread) {
				result.ensP10 = spread.p10
				result.ensP90 = spread.p90
				result.categoryShare = spread.categoryShare
				result.ensembleRun = ensemble.run
			}
		}
		if (this.context.baseElevation.loaded && forecast.length >= 2) {
			const elevation = this.context.baseElevation
			const landfall = findLandfall(forecast, (lonRad, latRad) => elevation.getElevation(new geographic(lonRad, latRad, 0)))
			if (landfall) result.landfallT = landfall.t
		}
		return result
	}

	/** Panel summary of the block; copies, since redux freezes its payloads */
	private summarize(node: StormColumnNode, forecast?: StormCutoutForecast): StormCutoutSummary {
		const layout = node.layout
		const summary: StormCutoutSummary = {
			lengthKm: layout.lengthKm,
			widthKm: layout.widthKm,
			heightScale: layout.heightScale,
			tickKm: layout.tickKm,
			motionDeg: Math.round(layout.strike / trig.rad)
		}
		const previous = engineBridge.getState()?.dialog.stormCutout?.summary
		summary.forecast = forecast ?? previous?.forecast
		summary.environment = this.columnEnvironment
		summary.buoys = this.columnBuoys.length > 0 ? this.columnBuoys : undefined
		summary.ships = previous?.ships ?? shipsSummary(parseShips(node.storm.ships))
		if (previous?.wind) {
			summary.wind = previous.wind
		} else if (node.windModel) {
			const radii = parseWindRadii(node.storm.wind_radii)
			const r34 = radius34Summary(radii)
			summary.wind = {
				source: node.windModel.source,
				rmwKm: Math.round(node.windModel.rmwKm),
				rmwSource: node.windModel.rmwSource,
				b: node.windModel.b.map(b => Math.round(b * 100) / 100) as [number, number, number, number],
				bestTrack: bestTrackSummary(parseBestTrack(node.storm.best_track)),
				r34MeanKm: r34 ? Math.round(r34.meanKm) : undefined,
				asymmetry: r34 ? Math.round(r34.asymmetry * 10) / 10 : undefined,
				ikeTJ: Math.round(integratedKineticEnergyTJ(node.windModel))
			}
		}
		const fields = this.columnFields
		if (fields.length > 0) {
			const sorted = [...fields].sort((a, b) => a.timeMs - b.timeMs)
			const displayed = node.displayedField ?? sorted[sorted.length - 1]
			summary.frames = {
				count: sorted.length,
				oldestIso: sorted[0].timeIso,
				newestIso: sorted[sorted.length - 1].timeIso,
				satellite: displayed.satellite,
				displayedIso: displayed.timeIso
			}
			const structure = displayed.structure
			summary.structure = {
				coldestTopC: Math.round(structure.coldestTopC),
				coldestTopKm: Math.round(structure.coldestTopKm * 10) / 10,
				coldFraction: structure.coldFraction,
				coldFractionTrend: sorted.map(f => ({ t: f.timeMs, fraction: f.structure.coldFraction })),
				eyeDiameterKm: structure.eyeDiameterKm,
				eyeContrastK: structure.eyeContrastK
			}
		}
		if (this.columnFlashes) {
			// "The last hour" ends at the scene clock, so scrubbing the
			// time-lapse moves the lightning facts with the lid
			const clockMs = this.context.clock.clockTime.date.getTime()
			const center = { lat: node.storm.lat, lon: node.storm.lon }
			summary.lightning = lightningSummary(this.columnFlashes.flashes, this.columnFlashes.observed, center, clockMs, glmCovers(center.lat, center.lon))
		}
		return summary
	}

	/** Dev debug hook: state of the open column */
	public static debugSnapshot(): Record<string, unknown> | null {
		const layer = HurricanesDatasetLayer.live
		const node = layer?.column
		if (!layer || !node) return null
		const displayed = node.displayedField
		return {
			stormId: layer.columnStormId,
			layout: node.layout,
			windModel: node.windModel ?? null,
			frames: layer.columnFields.length,
			displayedFrame: displayed ? { time: displayed.timeIso, satellite: displayed.satellite, structure: displayed.structure } : null,
			lightning: layer.columnFlashes ? { flashes: layer.columnFlashes.flashes.length, ...node.lightningCounts } : null,
			focused: layer.context.focusManager.getFocusedNode() === node,
			worldPosition: node.transform.position
		}
	}

	// ==================== Sync Configuration ====================

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "hurricanes",
			pollIntervalMs: 300_000, // 5 minutes; advisories publish every 3-6 hours
			needsSync: true,
			supportsDelta: true,
			deltaOnlyIngest: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await HurricanesTable.create(db)
	}

	public async applyCheckpoint(
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return

		const url = this.resolveUrl(shard.url, shard.path)
		const rows = await downloadArrowRows(url)
		await HurricanesTable.replaceAll(this.parseStormRows(rows))
	}

	public async applyDelta(
		delta: DatasetDelta,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		// Full active-storm snapshot each publish (delta-only protocol),
		// so replaceAll also drops storms that dissipated out of the feed
		const upsertsUrl = this.resolveUrl(delta.upserts.url, delta.upserts.path)
		const upsertRows = await downloadArrowRows(upsertsUrl)
		const storms = this.parseStormRows(upsertRows)
		await this.notifyNewAdvisories(storms)
		await HurricanesTable.replaceAll(storms)

		if (delta.deletes?.path) {
			const deletesUrl = this.resolveUrl(delta.deletes.url, delta.deletes.path)
			const deletesRows = await downloadArrowRows(deletesUrl)
			const deletes = (deletesRows as Array<{ id: string }>).map(r => String(r.id))
			if (deletes.length > 0) {
				await HurricanesTable.deleteByIds(deletes)
			}
		}
	}

	/**
	 * Toast when a fresh sync carries a new advisory or a brand new storm,
	 * so someone watching the map hears about updates without polling the
	 * browser panel. Skipped on first sync (everything would be "new") and
	 * while the hurricanes overlay is hidden.
	 */
	private async notifyNewAdvisories(storms: StormProperties[]): Promise<void> {
		if (!this.visible) return

		const existing = await HurricanesTable.getAll()
		if (existing.length === 0) return
		const previous = new Map(existing.map(s => [s.id, s]))

		const updates: StormAdvisoryNotification[] = []
		for (const storm of storms) {
			const old = previous.get(storm.id)
			if (!old) {
				updates.push({ id: storm.id, label: `${storm.classification} ${storm.name}` })
			} else if (old.advisory_date !== storm.advisory_date || old.advisory_num !== storm.advisory_num) {
				const advisory = storm.advisory_num ? ` · Advisory ${storm.advisory_num}` : ""
				updates.push({ id: storm.id, label: `${storm.classification} ${storm.name}${advisory}` })
			}
		}

		engineNotifier.stormAdvisories(updates)
	}

	private parseStormRows(arrowRows: ArrowRow[]): StormProperties[] {
		const rows = arrowRows as StormRow[]
		return rows.map(r => ({
			id: String(r.id),
			name: String(r.name),
			basin: String(r.basin),
			storm_num: Number(r.storm_num),
			classification: String(r.classification),
			advisory_num: r.advisory_num ? String(r.advisory_num) : null,
			advisory_date: String(r.advisory_date),
			lat: Number(r.lat),
			lon: Number(r.lon),
			max_wind_kt: r.max_wind_kt === null || r.max_wind_kt === undefined ? null : Number(r.max_wind_kt),
			gust_kt: r.gust_kt === null || r.gust_kt === undefined ? null : Number(r.gust_kt),
			mslp_mb: r.mslp_mb === null || r.mslp_mb === undefined ? null : Number(r.mslp_mb),
			category: Number(r.category ?? 0),
			movement_dir: r.movement_dir === null || r.movement_dir === undefined ? null : Number(r.movement_dir),
			movement_speed_kt: r.movement_speed_kt === null || r.movement_speed_kt === undefined ? null : Number(r.movement_speed_kt),
			observed_track: String(r.observed_track ?? "[]"),
			forecast_track: String(r.forecast_track ?? "[]"),
			cone: r.cone ? String(r.cone) : null,
			watches_warnings: r.watches_warnings ? String(r.watches_warnings) : null,
			wind_radii: r.wind_radii ? String(r.wind_radii) : null,
			headline: r.headline ? String(r.headline) : null,
			ensemble_tracks: r.ensemble_tracks ? String(r.ensemble_tracks) : null,
			ships: r.ships ? String(r.ships) : null,
			best_track: r.best_track ? String(r.best_track) : null,
			wind_probability: r.wind_probability ? String(r.wind_probability) : null,
			bbox_min_lon: Number(r.bbox_min_lon),
			bbox_min_lat: Number(r.bbox_min_lat),
			bbox_max_lon: Number(r.bbox_max_lon),
			bbox_max_lat: Number(r.bbox_max_lat),
			update_key: String(r.update_key),
			ingested_at: String(r.ingested_at),
		}))
	}

	// ==================== Scene Objects ====================

	public async queryFeatures(db: Database): Promise<StormProperties[]> {
		// There are never more than a couple dozen active storms globally;
		// each feature is a marker plus track polylines and a cone mesh
		const rows = await db.exec(`
			select *
			from hurricanes
			order by advisory_date desc
			limit 50
		`) as StormProperties[]

		return rows
	}

	public override async loadSceneObjectById(id: string): Promise<DatasetSceneObject<StormProperties> | undefined> {
		const existing = this.getSceneObject(id)
		if (existing) {
			return existing
		}

		const storm = await HurricanesTable.getById(id)
		if (!storm) return undefined
		this.addSceneObject(storm)
		return this.sceneObjects.get(id)
	}

	/**
	 * Video render mode: selected-storm presentation (bright cone, labeled
	 * forecast points) for one storm without giving it camera focus; every
	 * other storm's emphasis is cleared
	 */
	public setForecastEmphasis(stormId: string | undefined, on: boolean): void {
		for (const [id, sceneObject] of this.sceneObjects) {
			if (sceneObject instanceof StormFeature) {
				sceneObject.setEmphasis(on && id === stormId)
			}
		}
	}

	public createSceneObject(feature: StormProperties): DatasetSceneObject<StormProperties> | undefined {
		const storm = new StormFeature(this.context, feature)
		storm.setWindProbabilityThreshold(this.windProbabilityKt)
		return storm
	}
}

/** Structure at the newest fix plus the RMW trend, for the HUD */
function bestTrackSummary(track: BestTrack | null): StormCutoutBestTrack | undefined {
	if (!track || track.fixes.length === 0) return undefined
	const fixes = track.fixes
	const newest = fixes[fixes.length - 1]
	const newestMs = Date.parse(newest.t)
	const rmwAt = (hoursBack: number): number | null => {
		const target = newestMs - hoursBack * 3600_000
		let best: { dt: number; rmw: number } | undefined
		for (const f of fixes) {
			if (f.rmwKm === null) continue
			const dt = Math.abs(Date.parse(f.t) - target)
			if (dt <= 3.5 * 3600_000 && (!best || dt < best.dt)) best = { dt, rmw: f.rmwKm }
		}
		return best?.rmw ?? null
	}
	return {
		fixIso: newest.t,
		rmwKm: newest.rmwKm,
		rmw12hKm: rmwAt(12),
		rmw24hKm: rmwAt(24),
		eyeKm: newest.eyeKm,
		pociMb: newest.pociMb,
		rociKm: newest.rociKm,
		gustKt: newest.gustKt,
		depth: newest.depth,
		rmwSeries: fixes.filter(f => f.rmwKm !== null).map(f => [Date.parse(f.t), f.rmwKm!] as [number, number])
	}
}

/** The SHIPS checklist at the analysis hour (and shear a day out), for the HUD */
function shipsSummary(ships: ShipsDiagnostics | null): StormCutoutShips | undefined {
	if (!ships) return undefined
	const at = (values: Array<number | null>, tau: number): number | null => {
		const i = ships.taus.indexOf(tau)
		return i >= 0 ? values[i] ?? null : null
	}
	return {
		run: ships.run,
		shearKt: at(ships.shearKt, 0),
		shearDirDeg: at(ships.shearDirDeg, 0),
		shear24Kt: at(ships.shearKt, 24),
		sstC: at(ships.sstC, 0),
		potentialIntensityKt: at(ships.potentialIntensityKt, 0),
		rhMidPct: at(ships.rhMid, 0),
		oceanHeatContent: at(ships.oceanHeatContent, 0),
		landKm: at(ships.landKm, 0),
		ri: ships.ri
	}
}

/**
 * Which frames to decode for the lid, newest first: every frame within the
 * dense window behind the newest, then one per sparse step further back.
 * Rows arrive oldest first
 */
export function selectColumnFrames(rows: StormCloudProperties[]): StormCloudProperties[] {
	if (rows.length === 0) return []
	const newestMs = Date.parse(rows[rows.length - 1].time)
	const denseCutoff = newestMs - cColumnDenseHours * 3600_000
	const sparseMs = cColumnSparseMinutes * 60_000
	const selected: StormCloudProperties[] = []
	let lastSparseSlot = Number.NaN
	for (let i = rows.length - 1; i >= 0; i--) {
		const row = rows[i]
		const t = Date.parse(row.time)
		if (t >= denseCutoff) {
			selected.push(row)
			continue
		}
		const slot = Math.floor(t / sparseMs)
		if (slot !== lastSparseSlot) {
			lastSparseSlot = slot
			selected.push(row)
		}
	}
	return selected
}
